import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";
import { roleType } from "../constant/constants";
import { isEnvTrue } from "../constant/utils/commonUtils";
import {
  getSdfUserData,
  isCompanyPrivileged,
  setSdfUserData,
} from "../utils/roleUtils";

// ✅ Fetch all SDF Users
export const fetchSdfUsers = createAsyncThunk(
  "sdfUsers/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data: userData, error: authError } =
        await supabase.auth.getUser();
      if (authError) {
        console.error("Authentication error:", authError.message);
        throw authError;
      }
      if (!userData?.user) {
        return rejectWithValue("No authenticated user found.");
      }
      const currentUserId = userData.user.id;
      // Step 2: Fetch all users excluding the logged-in user
      const { data, error } = await supabase
        .from("company_user")
        .select("*")
        .neq("user_id", String(currentUserId))
        .eq("role", roleType.COMPANY_ADMIN);

    
      if (error) {
        console.error("Error fetching users:", error.message);
        throw error;
      }
      const filteredUsers = data.filter((user) => user.id !== currentUserId);
      return filteredUsers;
    } catch (error) {
      console.error("Fetch SDF Users Error:", error.message);
      return rejectWithValue(error.message || "Failed to fetch users.");
    }
  }
);

export const refreshCurrentSdfUser = createAsyncThunk(
  "sdfUsers/refreshCurrent",
  async (_, { dispatch, rejectWithValue }) => {
    const current = getSdfUserData();
    if (!current?.id || !isCompanyPrivileged(current.role)) return null;

    const res = await dispatch(getSdfUserById(current.id));
    if (res.meta?.requestStatus === "fulfilled" && res.payload) {
      setSdfUserData(res.payload);
      return res.payload;
    }
    return rejectWithValue(res.error?.message || "Failed to refresh sdf user");
  },
);

export const getSdfUserById = createAsyncThunk(
  "sdfUsers/getSdfUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user") // Ensure this is your actual SDF users table name
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getSdfUserByUserId = createAsyncThunk(
  "sdfUsers/getSdfUserByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user") // Ensure this is your actual SDF users table name
        .select()
        .eq("user_id", userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getSdfUserByCompanyId = createAsyncThunk(
  "sdfUsers/getSdfUserByCompanyId",
  async (companyId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user")
        .select("*")
        .eq("company_id", companyId)
        .eq("role", roleType.COMPANY_ADMIN)
        .eq("status", "active")
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        const { data: sdfFallback, error: sdfError } = await supabase
          .from("company_user")
          .select("*")
          .eq("company_id", companyId)
          .eq("role", roleType.SDF)
          .eq("status", "active")
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (sdfError) throw sdfError;
        return sdfFallback;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCompanyRoleAssignments = createAsyncThunk(
  "sdfUsers/fetchCompanyRoleAssignments",
  async ({ companyId, role }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.rpc(
        "get_company_user_role_assignments",
        {
          p_company_id: companyId,
          p_role: role,
        },
      );

      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        id: row.company_user_id,
      }));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCompanySdfList = createAsyncThunk(
  "sdfUsers/fetchCompanySdfList",
  async (companyId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user")
        .select("*")
        .eq("company_id", companyId)
        .eq("role", roleType.SDF)
        .eq("status", "active");

      if (error) throw error;
      return data ?? [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCompanyAdminList = createAsyncThunk(
  "sdfUsers/fetchCompanyAdminList",
  async (companyId, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user")
        .select("*")
        .eq("company_id", companyId)
        .eq("role", roleType.COMPANY_ADMIN)
        .eq("status", "active");

      if (error) throw error;
      return data ?? [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const assignCompanyUserRoles = createAsyncThunk(
  "sdfUsers/assignCompanyUserRoles",
  async ({ companyId, companyUserIds, role }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("assign_company_user_roles", {
        p_company_id: companyId,
        p_company_user_ids: companyUserIds,
        p_role: role,
        p_assigned_by: userData?.user?.id ?? null,
      });
      dispatch(stopLoading());

      if (error) throw error;
      return data;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error.message);
    }
  },
);

export const assignSdfRoles = createAsyncThunk(
  "sdfUsers/assignSdfRoles",
  async ({ companyId, companyUserIds }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("assign_company_user_roles", {
        p_company_id: companyId,
        p_company_user_ids: companyUserIds,
        p_role: roleType.SDF,
        p_assigned_by: userData?.user?.id ?? null,
      });
      dispatch(stopLoading());

      if (error) throw error;
      return data;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error.message);
    }
  },
);

export const removeCompanyUserRoles = createAsyncThunk(
  "sdfUsers/removeCompanyUserRoles",
  async ({ companyId, companyUserIds, role }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("remove_company_user_roles", {
        p_company_id: companyId,
        p_company_user_ids: companyUserIds,
        p_role: role,
        p_removed_by: userData?.user?.id ?? null,
      });
      dispatch(stopLoading());

      if (error) throw error;
      return data;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error.message);
    }
  },
);

export const removeSdfRoles = createAsyncThunk(
  "sdfUsers/removeSdfRoles",
  async ({ companyId, companyUserIds }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("remove_company_user_roles", {
        p_company_id: companyId,
        p_company_user_ids: companyUserIds,
        p_role: roleType.SDF,
        p_removed_by: userData?.user?.id ?? null,
      });
      dispatch(stopLoading());

      if (error) throw error;
      return data;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error.message);
    }
  },
);

export const checkSdfEmailExists = createAsyncThunk(
  "sdf/checkSdfEmailExists",
  async (email, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company_user")
        .select("email")
        .eq("email", email.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116: 'exact one row not found' which is what we want
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Add New SDF User
export const addSdfUser = createAsyncThunk(
  "sdf/addSdfUser",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      
      
      const { first_name, last_name, email, contact_number, company_id, title, id_number, sdf_registration_number } = userData;
      

      let authData, authError;
     if (isEnvTrue("REACT_APP_SUPABASE_DEFAULT_PASSWORD")) {
       const res = await supabase.auth.admin.createUser({
         email: email.toLowerCase(),
         password: "Admin@123", // You need to set a password
         email_confirm: true, // This ensures the user is added without needing email confirmation
       });

       authData = res.data;
       authError = res.error;
     } else {
       // Step 1: Sign up the user with a dummy password
       const res = await supabase.auth.admin.inviteUserByEmail(
         email.toLowerCase(),
         {
          //  redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
           data: { role: roleType.COMPANY_ADMIN },
         }
       );

       authData = res.data;
       authError = res.error;
     }
      

      if (authError) throw authError;

      const userId = authData?.user?.id; // Get the newly created user's ID
      if (!userId) throw new Error("User ID not found");

      // Step 2: Insert the user into sdf_users table
      const insertData = {
        user_id: userId,
        company_id: company_id, // Company selected
        first_name: first_name,
        last_name: last_name,
        email: email.toLowerCase(),
        contact_number: contact_number,
        title: title || null,
        id_number: id_number || null,
        sdf_registration_number: sdf_registration_number || null,
        role: roleType.COMPANY_ADMIN,
      };
      const { data, error: insertError } = await supabase
        .from("company_user")
        .insert(insertData)
        .select();
      dispatch(stopLoading());

      if (insertError) {
        throw insertError;
      }

      return {
        success: true,
        message: "Company admin user added successfully!",
        data,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Update SDF User
export const updateSdfUser = createAsyncThunk(
  "sdfUsers/update",
  async ({ id, updatedData, oldEmail }, { rejectWithValue }) => {
    try {
      if (oldEmail != updatedData.email) {
        // Only delete existing auth user if user_id exists and is valid
        if (updatedData.user_id) {
          const { data, error } = await supabase.auth.admin.deleteUser(updatedData.user_id);
          if (error) throw error;
        }
        
        // Create new auth user with new email
        if (isEnvTrue("REACT_APP_SUPABASE_DEFAULT_PASSWORD")) {
          const { data: authData, error: authError } =
            await supabase.auth.admin.createUser({
              email: updatedData.email.toLowerCase(),
              password: "Admin@123",
              email_confirm: true,
            });
          if (authError) throw authError;
          updatedData.user_id = authData.user.id;
        } else {
          const { data: authData, error: authError } =
            await supabase.auth.admin.inviteUserByEmail(
              updatedData.email.toLowerCase(),
              {
                // redirectTo: `${process.env.REACT_APP_URL}/reset-password`,
                data: { role: roleType.COMPANY_ADMIN },
              }
            );
          if (authError) throw authError;
          updatedData.user_id = authData.user.id;
        }
      }

      const { data, error } = await supabase
        .from("company_user")
        .update(updatedData)
        .eq("id", id)
        .select();
        
      if (error) {
        console.error("updateSdfUser database error:", error);
        throw error;
      }
      
      return data[0];
    } catch (error) {
      console.error("updateSdfUser error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Delete SDF User
export const deleteSdfUser = createAsyncThunk(
  "sdfUsers/delete",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());

      const { data: companyUser, error: fetchError } = await supabase
        .from("company_user")
        .select("user_id")
        .eq("id", id)
        .single();
      if (fetchError) throw fetchError;

      const { data: userData } = await supabase.auth.getUser();
      const { data, error } = await supabase.rpc("delete_company_admin_user", {
        p_company_user_id: id,
        p_removed_by: userData?.user?.id ?? null,
      });
      dispatch(stopLoading());

      if (error) throw error;

      if (data?.action === "deleted" && companyUser?.user_id) {
        const { error: authDeleteError } = await supabase.auth.admin.deleteUser(
          companyUser.user_id,
        );
        if (authDeleteError) throw authDeleteError;
      }

      return id;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error.message);
    }
  },
);

const sdfSlice = createSlice({
  name: "sdfUsers",
  initialState: {
    sdfUsers: [],
    companySdfList: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSdfUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSdfUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.sdfUsers = action.payload;
      })
      .addCase(fetchSdfUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addSdfUser.fulfilled, (state, action) => {
        state.sdfUsers.push(action.payload.data[0]);
      })
      .addCase(updateSdfUser.fulfilled, (state, action) => {
        state.sdfUsers = state.sdfUsers.map((user) =>
          user.id === action.payload.id ? action.payload : user
        );
      })
      .addCase(deleteSdfUser.fulfilled, (state, action) => {
        state.sdfUsers = state.sdfUsers.filter(
          (user) => user.id !== action.payload
        );
      })

      // ✅ Get SDF User By Company ID
      .addCase(getSdfUserByCompanyId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSdfUserByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        // You might want to store this in a separate field if needed
      })
      .addCase(getSdfUserByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanySdfList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanySdfList.fulfilled, (state, action) => {
        state.loading = false;
        state.companySdfList = action.payload;
      })
      .addCase(fetchCompanySdfList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanyAdminList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyAdminList.fulfilled, (state, action) => {
        state.loading = false;
        state.companySdfList = action.payload;
      })
      .addCase(fetchCompanyAdminList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default sdfSlice.reducer;
